import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import QuizApi from '../../api/QuizApi'
import Card from '../../components/Card'
import Table from '../../components/table'
import texts from '../../texts'

const FlipCards = ({ vocabFile = 'vocabulario.json', darkMode = false }) => {
  const { category } = useParams()
  const [data, setData] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [finished, setFinished] = useState(false)
  const [showResultsDirect, setShowResultsDirect] = useState(false)
  const [reviewingWord, setReviewingWord] = useState(null)
  const [reviewMessage, setReviewMessage] = useState('')

  // Función para extraer tarjetas de cualquier estructura
  function getCardsFromCategory(jsonData, category) {
    const catData = jsonData[category]
    let cards = []

    if (!catData) return cards

    // Si todos los valores son objetos, es anidado (subgrupos)
    if (Object.values(catData).every((val) => typeof val === 'object')) {
      Object.entries(catData).forEach(([subcat, subObj]) => {
        Object.entries(subObj).forEach(([hiragana, romaji], idx) => {
          cards.push({
            hiragana,
            romaji,
            subcat,
            correct: null,
            originalIndex: idx // <-- aquí guardas el orden
          })
        })
      })
    } else {
      // Categoría plana
      Object.entries(catData).forEach(([hiragana, romaji], idx) => {
        cards.push({
          hiragana,
          romaji,
          subcat: category,
          correct: null,
          originalIndex: idx // <-- aquí guardas el orden
        })
      })
    }
    return cards
  }

  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[array[i], array[j]] = [array[j], array[i]]
    }
    return array
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`/data/${vocabFile}`)
        const jsonData = await response.json()
        if (category && jsonData[category]) {
          const cards = getCardsFromCategory(jsonData, category)
          setData(shuffleArray(cards))
          setCurrentIndex(0)
          setFinished(false)
        }
      } catch (error) {
        console.error('Error al cargar los datos:', error)
      }
    }
    fetchData()
  }, [category, vocabFile])

  useEffect(() => {
    setShowResultsDirect(false)
    setFinished(false)
    setCurrentIndex(0)
  }, [category, vocabFile])

  const handleAnswer = (isCorrect) => {
    const updatedData = [...data]
    updatedData[currentIndex].correct = isCorrect ? '○' : '×'
    if (currentIndex + 1 < data.length) {
      setCurrentIndex((prev) => prev + 1)
    } else {
      setFinished(true)
    }
  }

  const handleRestart = () => {
    setFinished(false)
    setShowResultsDirect(false)
    setCurrentIndex(0)
    setData(shuffleArray(data))
  }

  const handleReviewWord = async (card) => {
    const word = card.hiragana || card.romaji
    setReviewingWord(word)
    setReviewMessage('')

    try {
      await QuizApi.markWordForReview({
        palabra_castellano: card.castellano || '',
        hiragana: card.hiragana,
        kanji: card.kanji || null,
        romaji: card.romaji || '',
        source: 'flashcards',
        category,
        notes: `${card.romaji || ''} (${card.hiragana})`,
        user_id: null
      })
      setReviewMessage(`Se marcó “${word}” para repasar con S.`)
    } catch (error) {
      setReviewMessage(`No se pudo marcar “${word}” para repasar.`)
    } finally {
      setReviewingWord(null)
    }
  }

  // Columnas para la tabla
  const columns = [
    ...('Unidad 11-2' === category ? [{ Header: 'Subcategoría', accessor: 'subcat' }] : []),
    { Header: 'Palabra', accessor: 'hiragana', sortable: true },
    { Header: 'Traducción', accessor: 'romaji' },
    {
      Header: 'Resultado',
      accessor: 'correct',
      Cell: ({ value }) => (
        <span className={value === '○' ? 'solo-correct' : value === '×' ? 'solo-incorrect' : ''}>
          {value === '○' || value === '×' ? value : '-'}
        </span>
      )
    },
    {
      Header: 'Acción',
      accessor: 'review',
      Cell: ({ row }) => (
        <button
          onClick={() => handleReviewWord(row)}
          disabled={reviewingWord === row.hiragana}
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid #1d4ed8',
            background: '#eff6ff',
            color: '#1d4ed8',
            cursor: 'pointer'
          }}
        >
          {reviewingWord === row.hiragana ? 'Enviando...' : 'Marcar S'}
        </button>
      )
    }
  ]

  const sortedResults = [...data].sort((a, b) => a.originalIndex - b.originalIndex)

  return (
    <div style={{ paddingTop: '30px' }}>
      {!finished && !showResultsDirect && data.length > 0 && (
        <>
          <div style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '1.1em' }}>
            {currentIndex + 1} / {data.length}
          </div>
          <Card
            key={`${category}-${currentIndex}`} // <-- Esto fuerza el reinicio del estado interno
            hiragana={data[currentIndex].hiragana}
            romaji={data[currentIndex].romaji}
            subcat={data[currentIndex].subcat}
            onAnswer={handleAnswer}
            darkMode={darkMode}
          />
          <button
            onClick={() => setShowResultsDirect(true)}
            style={{
              marginTop: 50,
              padding: '10px 24px',
              fontSize: '1.1em',
              background: '#181a1b',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer'
            }}
          >
            {texts.flip.verResults}
          </button>
        </>
      )}
      {(finished || showResultsDirect) && (
        <div style={{ maxWidth: 700, margin: 'auto' }}>
          <h2>{texts.flip.resultados}</h2>
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <Table columns={columns} data={sortedResults} />
          </div>
          {reviewMessage && (
            <p style={{ marginTop: 12, color: '#0a7d2e', fontWeight: 600 }}>{reviewMessage}</p>
          )}
          <button
            onClick={handleRestart}
            style={{
              marginTop: '20px',
              padding: '10px 24px',
              fontSize: '1.1em',
              background: '#181a1b',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer'
            }}
          >
            {texts.flip.reiniciar}
          </button>
        </div>
      )}
      {data.length === 0 && <p>Cargando datos...</p>}
    </div>
  )
}

export default FlipCards
